/**
 * C01. Streak + Loss Aversion — main orchestrator (學習端 only).
 *
 * Spec: docs/GAME_TECHNIQUES_WIKI.md §C01.
 * Pillar: Retention (D7 13% → 18-22%, per Duolingo benchmark).
 *
 * Responsibility split:
 *   - `shared/progress.ts`  — state machine (`tickStreakOnLoad`, `isStreakAtRisk`,
 *                              `streakMilestoneCrossed`, STREAK_MILESTONES).
 *                              These live next to PlayerProgress because they
 *                              mutate it; this file does NOT duplicate state.
 *   - `streak.ts` (here)    — high-level "wake-on-load" orchestrator. Captures
 *                              prevDays, ticks, persists, surfaces a snapshot the
 *                              UI + mascot can consume in one shot.
 *   - `streak-ui.ts`        — DOM banner (🔥 icon + counter + at-risk warning).
 *
 * 🔥 學習與遊戲分離 (CLAUDE.md 紅線):
 *   This module ONLY touches PlayerProgress + DOM. It does NOT import anything
 *   from `src/game/`. The mascot integration is opt-in by the home page,
 *   wired through a window event (`ef:streak-milestone`) so home.ts can decide
 *   whether to fire the mascot celebration — keeping this file decoupled.
 */

import type { PlayerProgress } from '../../shared/types';
import {
  tickStreakOnLoad,
  saveProgress,
  isStreakAtRisk,
  streakMilestoneCrossed,
  hoursSinceLastLogin,
  STREAK_AT_RISK_HOURS,
  STREAK_MILESTONES,
} from '../../shared/progress';

/** Snapshot returned by `processStreakOnHomeEnter` for UI/mascot consumption. */
export interface StreakSnapshot {
  /** Current streak (days) AFTER ticking. */
  days: number;
  /** Historical best streak (max of all-time and current). */
  longest: number;
  /** True iff the kid is in the 20-48h "save your streak!" window. */
  atRisk: boolean;
  /** Hours since the last login (0 right after a tick that bumped lastLoginISO). */
  hoursSinceLastLogin: number;
  /** Milestone tier (7 / 30 / 100) the kid JUST crossed on this tick, else null. */
  justCrossedMilestone: number | null;
  /** Next upcoming milestone target (7 / 30 / 100), or null if past 100. */
  nextMilestone: number | null;
  /** Days remaining to the next milestone (0 if just hit one or no next). */
  daysToNextMilestone: number;
}

/**
 * Custom event payload for `ef:streak-milestone`.
 *
 * Fired by `processStreakOnHomeEnter` when the kid just crossed 7 / 30 / 100.
 * home.ts listens and triggers the mascot 'streak-celebrate' state — this
 * indirection keeps streak.ts from importing the mascot module directly
 * (which would couple state to UI side-effects).
 */
export interface StreakMilestoneEventDetail {
  tier: number;
  days: number;
  longest: number;
}

/**
 * Single entry point called from home page (and main.ts boot, if desired).
 * Idempotent within the same UTC day: a second call on the same day is a no-op
 * because `tickStreakOnLoad` short-circuits when `lastLoginISO`'s date == today.
 *
 * Side effects:
 *   1. Mutates `p.streak.{days, lastLoginISO, longest}` via tickStreakOnLoad
 *   2. Calls `saveProgress(p)` IFF the tick advanced state (days or longest changed)
 *   3. Dispatches `ef:streak-milestone` on `window` when a 7/30/100 tier is crossed
 *
 * The save-only-on-change rule keeps test environments (jsdom without
 * localStorage hooks) free of redundant writes during repeat home renders.
 *
 * @returns A fresh snapshot the caller can hand straight to streak-ui.
 */
export function processStreakOnHomeEnter(
  p: PlayerProgress,
  nowMs: number = Date.now(),
): StreakSnapshot {
  // Capture before-state for milestone diff
  const prevDays = p.streak?.days ?? 0;
  const prevLongest = p.streak?.longest ?? 0;
  const prevLastLogin = p.streak?.lastLoginISO ?? '';

  // Tick (uses ISO string for backward-compat with existing call sites)
  tickStreakOnLoad(p, new Date(nowMs).toISOString());

  const days = p.streak?.days ?? 0;
  const longest = p.streak?.longest ?? 0;

  // Persist only when something actually changed (avoids triggering save
  // listeners on no-op re-renders within the same day).
  const changed =
    days !== prevDays ||
    longest !== prevLongest ||
    (p.streak?.lastLoginISO ?? '') !== prevLastLogin;
  if (changed) saveProgress(p);

  const justCrossed = streakMilestoneCrossed(prevDays, days);

  // Fire the milestone event if we crossed a tier — home page wires this
  // to mascot.cheer('streak-celebrate'). Use a `try` since `window` may
  // not exist in pure-state unit tests that don't include the DOM env.
  if (justCrossed !== null && typeof window !== 'undefined') {
    try {
      const detail: StreakMilestoneEventDetail = {
        tier: justCrossed,
        days,
        longest,
      };
      window.dispatchEvent(new CustomEvent('ef:streak-milestone', { detail }));
    } catch {
      // jsdom environments without CustomEvent shim — ignore, the snapshot
      // already carries `justCrossedMilestone` for the caller.
    }
  }

  const next = nextMilestone(days);

  return {
    days,
    longest,
    atRisk: isStreakAtRisk(p, nowMs),
    hoursSinceLastLogin: hoursSinceLastLogin(p, nowMs),
    justCrossedMilestone: justCrossed,
    nextMilestone: next,
    daysToNextMilestone: next === null ? 0 : Math.max(0, next - days),
  };
}

/**
 * Next milestone (7 / 30 / 100) strictly greater than `days`.
 * Returns null once past 100 (kid is in legendary territory — UI can switch
 * to a "🐉 傳說大俠" copy instead of a countdown).
 */
export function nextMilestone(days: number): number | null {
  for (const tier of STREAK_MILESTONES) {
    if (days < tier) return tier;
  }
  return null;
}

/**
 * Snapshot WITHOUT mutating state — used by callers that just want to render
 * the current banner (e.g. dashboard, second home re-render in the same SPA
 * session). Read-only counterpart to `processStreakOnHomeEnter`.
 */
export function snapshotStreak(p: PlayerProgress, nowMs: number = Date.now()): StreakSnapshot {
  const days = p.streak?.days ?? 0;
  const longest = p.streak?.longest ?? 0;
  const next = nextMilestone(days);
  return {
    days,
    longest,
    atRisk: isStreakAtRisk(p, nowMs),
    hoursSinceLastLogin: hoursSinceLastLogin(p, nowMs),
    justCrossedMilestone: null,
    nextMilestone: next,
    daysToNextMilestone: next === null ? 0 : Math.max(0, next - days),
  };
}

// Re-export the constants so banner UI + tests can pull everything from
// this single module surface.
export { STREAK_AT_RISK_HOURS, STREAK_MILESTONES };
